import type { ArtifactAction, ProjectMessage } from '../types'
import { getTimestamp } from '@/utils/debug-logger'

// Re-export JSON cleaning utilities
export { extractAndRemoveJSON, cleanStreamText } from './_removeJson'

export type ParseAIStreamOptions = {
  onResponse?: (text: string) => void
  onGetUserMessage?: (userMessage: ProjectMessage) => void
  onGetAssistantMessage?: (content: string) => void
  onArtifactStart?: () => void
  onArtifactEnd?: () => void
  onActionStart?: (action: ArtifactAction) => void
  onActionEnd?: (action: ArtifactAction) => Promise<void> | void
  onAfterArtifactEnd?: (content: string) => void
  onProcessText?: (text: string, chunk: string) => void
  onImprovingActionStart?: () => void
  onImprovingActionEnd?: () => void
  onGetSuperPromptIdsAction?: (superPromptIds: string[]) => void
  onGetSuperPromptFileNamesAction?: (superPromptFileNames: string[]) => void
  onReasoning?: (reasoning: string) => void
  onReasoningTools?: (
    tools: Array<{ icon?: string; icon_name?: string; statement: string }>,
  ) => void
}

export type ProcessStateContext = {
  artifactStarted: boolean
  artifactEnded: boolean
  processedActions: Set<string>
  startedActions: Set<string>
  streamedResponseLength: number
  afterArtifactStreamedLength: number
  hasTriggeredImproving: boolean
  hasProcessedAssistantMessage: boolean
}

/**
 * text 是完整的流式响应
 * chunk 是流式响应的每一段
   text 的示例数据：
   <UserMessage>xxxx</UserMessage>
   <action type="create" path="source"></action>
   <action type="features" content="["feature1", "feature2"]"></action>
   <action type="rewrite" path="xxx.json"></action>
   <action type="done_features" content="[]"></action>
 */

export const processAIStreamChunk = async (
  text: string,
  chunk: string,
  state: ProcessStateContext,
  options?: ParseAIStreamOptions,
): Promise<ProcessStateContext> => {
  const {
    onResponse,
    onGetUserMessage,
    onGetAssistantMessage,
    onArtifactStart,
    onArtifactEnd,
    onActionStart,
    onActionEnd,
    onAfterArtifactEnd,
    onProcessText,
    onImprovingActionStart,
    onImprovingActionEnd,
    onGetSuperPromptIdsAction,
    onGetSuperPromptFileNamesAction,
  } = options ?? {}

  let {
    artifactStarted,
    artifactEnded,
    processedActions,
    startedActions,
    streamedResponseLength,
    afterArtifactStreamedLength,
    hasTriggeredImproving,
    hasProcessedAssistantMessage,
  } = state

  const userMessageMatch = text.match(/<UserMessage>([\s\S]+)<\/UserMessage>/)
  if (userMessageMatch) {
    const userMessage = JSON.parse(userMessageMatch[1]) as ProjectMessage
    onGetUserMessage?.(userMessage)
  }

  // Extract assistant message wrapped in <assistant></assistant> tags
  // Only process it once to avoid duplicates
  const assistantMessageMatch = text.match(/<assistant>([\s\S]+?)<\/assistant>/)

  // Debug: Log assistant message detection
  if (text.includes('<assistant>') || text.includes('</assistant>')) {
    console.log('[processAIStreamChunk] 📝 Assistant tag detection:', {
      hasOpenTag: text.includes('<assistant>'),
      hasCloseTag: text.includes('</assistant>'),
      matchFound: !!assistantMessageMatch,
      hasCallback: !!onGetAssistantMessage,
      alreadyProcessed: hasProcessedAssistantMessage,
      textLength: text.length,
    })
  }

  if (
    assistantMessageMatch &&
    onGetAssistantMessage &&
    !hasProcessedAssistantMessage
  ) {
    const assistantContent = assistantMessageMatch[1].trim()
    console.log(
      '[processAIStreamChunk] 📝 Calling onGetAssistantMessage with content:',
      {
        contentLength: assistantContent.length,
        contentPreview: assistantContent.slice(0, 200),
      },
    )
    onGetAssistantMessage(assistantContent)
    hasProcessedAssistantMessage = true
  }

  onProcessText?.(text, chunk)
  const IMPROVING_ACTION_TEXT = '<System>Improving implementation</System>'
  const isImproving = text.includes(IMPROVING_ACTION_TEXT)

  // Use the full text for processing, not just the part before improving marker
  // This ensures we capture all actions, including those during the improving phase
  const processingText = text

  let artifactStartIndex = processingText.indexOf('<artifact>')
  let artifactEndIndex = processingText.indexOf('</artifact>')

  // Support multiple artifact rounds (e.g., after retries/fixes)
  const latestArtifactStartIndex = processingText.lastIndexOf('<artifact>')
  const latestArtifactEndIndex = processingText.lastIndexOf('</artifact>')
  const hasNewArtifactAfterPrevious =
    artifactEnded &&
    latestArtifactStartIndex !== -1 &&
    latestArtifactStartIndex > artifactEndIndex

  if (hasNewArtifactAfterPrevious) {
    console.log(
      '[processAIStreamChunk] 🔄 New <artifact> detected after end, resetting artifact state for next round',
    )
    artifactStarted = false
    artifactEnded = false
    processedActions = new Set<string>()
    startedActions = new Set<string>()
    streamedResponseLength = 0
    afterArtifactStreamedLength = 0
    hasProcessedAssistantMessage = false
    artifactStartIndex = latestArtifactStartIndex
    artifactEndIndex = processingText.indexOf('</artifact>', artifactStartIndex)
  } else {
    artifactStartIndex =
      latestArtifactStartIndex !== -1
        ? latestArtifactStartIndex
        : artifactStartIndex
    artifactEndIndex =
      latestArtifactEndIndex !== -1 ? latestArtifactEndIndex : artifactEndIndex
  }

  // Debug: Log artifact tag detection
  if (
    processingText.includes('<action') &&
    !artifactStarted &&
    artifactStartIndex === -1
  ) {
    console.log('[processAIStreamChunk] ⚠️ Has <action> but no <artifact>:', {
      hasArtifactTag: artifactStartIndex !== -1,
      hasArtifactEndTag: artifactEndIndex !== -1,
      textPreview: processingText.slice(0, 300),
    })
  }

  // Stream response text progressively before artifact
  if (!artifactStarted) {
    const responseEndIndex =
      artifactStartIndex !== -1 ? artifactStartIndex : processingText.length
    const currentText = processingText
      .slice(0, responseEndIndex)
      .replace(/<System>[\s\S]*?<\/System>/g, '')
      .replace(/<UserMessage>[\s\S]*?<\/UserMessage>/g, '')
      .trim()

    if (currentText.length > streamedResponseLength) {
      // Stream only the new portion
      const newContent = currentText.slice(streamedResponseLength)
      if (newContent) {
        onResponse?.(currentText) // Send the full accumulated text
        streamedResponseLength = currentText.length
      }
    }
  }

  if (artifactStartIndex !== -1 && !artifactStarted) {
    onArtifactStart?.()
    artifactStarted = true
  }

  const actionStartMatches = Array.from(
    processingText.matchAll(/<action([^>]+)>/g),
  )
  for (let i = 0; i < actionStartMatches.length; i++) {
    const match = actionStartMatches[i]
    const [_, attrs] = match
    const actionProps: { [key: string]: string } = {}

    const attrMatches = Array.from(attrs.matchAll(/(\w+)="([^"]+)"/g))
    const actionKeyParts = []
    for (const attrMatch of attrMatches) {
      const [_, key, value] = attrMatch
      actionProps[key] = value
      actionKeyParts.push(`${key}-${value}-${i}`)
    }
    const actionKey = actionKeyParts.join('-')

    const isSpecialAction =
      actionProps.type === 'features' || actionProps.type === 'done_features'
    if (!startedActions.has(actionKey) && !isSpecialAction) {
      onActionStart?.({
        key: actionKey,
        ...actionProps,
      } as ArtifactAction)
      startedActions.add(actionKey)
    }
  }

  const completeActionMatches = Array.from(
    text.matchAll(/<action([^>]+)>([\s\S]*?)<\/action>/g),
  )
  for (let i = 0; i < completeActionMatches.length; i++) {
    const match = completeActionMatches[i]
    const [_, attrs, content] = match
    const actionProps: { [key: string]: string } = {}

    // attrs 示例数据: type=\"features\" content=\"68a91e623ebdef84d82cffee,68a91e623ebdef84d82cffe1]\"
    const attrMatches = Array.from(attrs.matchAll(/(\w+)="([^"]+)"/g))
    const actionKeyParts = []
    for (const attrMatch of attrMatches) {
      const [_, key, value] = attrMatch
      actionProps[key] = value
      actionKeyParts.push(`${key}-${value}-${i}`)
    }
    const actionKey = actionKeyParts.join('-')

    if (!processedActions.has(actionKey)) {
      const isSpecialAction =
        actionProps.type === 'features' || actionProps.type === 'done_features'

      if (!isSpecialAction) {
        await onActionEnd?.({
          key: actionKey,
          ...actionProps,
          content: content?.trim(),
        } as ArtifactAction)
      }

      // 处理 features action
      if (actionProps.type === 'features' && onGetSuperPromptIdsAction) {
        try {
          const superPromptIds = (actionProps?.content || '')
            .split(',')
            .filter(Boolean) as string[]
          onGetSuperPromptIdsAction(superPromptIds)
        } catch (error) {
          console.error('Failed to parse features:', error)
        }
      }
      // 处理 done_features action
      if (
        actionProps.type === 'done_features' &&
        onGetSuperPromptFileNamesAction
      ) {
        try {
          const superPromptFileNames = (actionProps?.content || '')
            .split(',')
            .filter(Boolean) as string[]
          onGetSuperPromptFileNamesAction(superPromptFileNames)
        } catch (error) {
          console.error('Failed to parse done_features:', error)
        }
      }

      processedActions.add(actionKey)
    }
  }

  // Only trigger once per stream session
  if (isImproving && !hasTriggeredImproving) {
    hasTriggeredImproving = true
    onImprovingActionStart?.()
  }

  if (artifactEndIndex !== -1 && !artifactEnded) {
    console.log('[processAIStreamChunk] 🔚 </artifact> detected:', {
      artifactStarted,
      artifactEndIndex,
      textAfterArtifactPreview: text.slice(artifactEndIndex),
    })
    onArtifactEnd?.()
    artifactEnded = true
    // Reset the response streaming for content after artifact
    streamedResponseLength = 0
  }

  // Stream text after artifact progressively
  if (artifactEnded && artifactEndIndex !== -1) {
    const fullTextAfterArtifact = text.slice(
      artifactEndIndex + '</artifact>'.length,
    )
    const cleanTextAfterArtifact = fullTextAfterArtifact
      .replace(/<System>[\s\S]*?<\/System>/g, '')
      .replace(/<UserMessage>[\s\S]*?<\/UserMessage>/g, '')
      .trim()

    if (cleanTextAfterArtifact.length > afterArtifactStreamedLength) {
      // Stream the accumulated text after artifact
      console.log(
        '[processAIStreamChunk] 📡 Calling onResponse with after-artifact text:',
        {
          cleanTextLength: cleanTextAfterArtifact.length,
          previousLength: afterArtifactStreamedLength,
          newContentLength:
            cleanTextAfterArtifact.length - afterArtifactStreamedLength,
          textPreview: cleanTextAfterArtifact.slice(0, 150),
        },
      )
      onResponse?.(cleanTextAfterArtifact)
      afterArtifactStreamedLength = cleanTextAfterArtifact.length
    }

    // Still call onAfterArtifactEnd for backward compatibility
    if (cleanTextAfterArtifact && onAfterArtifactEnd) {
      console.log('[processAIStreamChunk] 🎬 Calling onAfterArtifactEnd:', {
        contentLength: cleanTextAfterArtifact.length,
        contentPreview: cleanTextAfterArtifact.slice(0, 150),
      })
      onAfterArtifactEnd(cleanTextAfterArtifact)
    }
  }

  return {
    artifactStarted,
    artifactEnded,
    processedActions,
    startedActions,
    streamedResponseLength,
    afterArtifactStreamedLength,
    hasTriggeredImproving,
    hasProcessedAssistantMessage,
  }
}

export const parseAIStream = async (
  aiStream: ReadableStream<Uint8Array>,
  options?: ParseAIStreamOptions,
) => {
  console.log(
    `${getTimestamp()} [parseAIStream] ===== STARTING STREAM PARSING =====`,
  )
  const reader = aiStream.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let textBuffer = '' // Buffer for actual text content (excludes reasoning chunks)

  const state: ProcessStateContext = {
    artifactStarted: false,
    artifactEnded: false,
    processedActions: new Set<string>(),
    startedActions: new Set<string>(),
    streamedResponseLength: 0,
    afterArtifactStreamedLength: 0,
    hasTriggeredImproving: false,
    hasProcessedAssistantMessage: false,
  }

  let chunkCount = 0
  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      console.log(
        `${getTimestamp()} [parseAIStream] Stream done signal received, ending parsing`,
      )
      console.log(`${getTimestamp()} [parseAIStream] Final state:`, {
        totalChunks: chunkCount,
        textBufferLength: textBuffer.length,
        artifactStarted: state.artifactStarted,
        artifactEnded: state.artifactEnded,
        hasTriggeredImproving: state.hasTriggeredImproving,
      })
      options?.onImprovingActionEnd?.()
      break
    }

    const chunk = decoder.decode(value, { stream: true })
    chunkCount++

    // Try to parse chunk as structured JSON for reasoning/reasoning_tools
    try {
      const parsed = JSON.parse(chunk)
      if (parsed.role === 'reasoning' && options?.onReasoning) {
        options.onReasoning(parsed.content)
        continue // Don't add to buffer
      }
      if (parsed.role === 'reasoning_tools' && options?.onReasoningTools) {
        options.onReasoningTools(parsed.content)
        continue // Don't add to buffer
      }
    } catch (e) {
      // Not JSON or different structure, treat as regular text
    }

    // Add to both buffers for non-reasoning content
    buffer += chunk
    textBuffer += chunk

    // Log every 10 chunks or if chunk contains important flags
    const hasImportantFlags =
      chunk.includes('<System>') || chunk.includes('</artifact>')
    if (chunkCount % 10 === 0 || hasImportantFlags) {
      console.log(
        `${getTimestamp()} [parseAIStream] Processing chunk ${chunkCount}:`,
        {
          chunkLength: chunk.length,
          bufferLength: buffer.length,
          textBufferLength: textBuffer.length,
          hasSystemTag: chunk.includes('<System>'),
          hasArtifactEnd: chunk.includes('</artifact>'),
          chunkPreview: chunk.slice(0, 100),
        },
      )
    }

    const updatedState = await processAIStreamChunk(
      textBuffer,
      chunk,
      state,
      options,
    )

    // console.log(`[parseAIStream] After processing chunk ${chunkCount}:`, {
    //   updatedStateHasProcessedAssistantMessage:
    //     updatedState.hasProcessedAssistantMessage,
    //   stateHasProcessedAssistantMessage: state.hasProcessedAssistantMessage,
    // })

    Object.assign(state, updatedState)
  }

  console.log(
    `${getTimestamp()} [parseAIStream] ===== STREAM PARSING COMPLETE =====`,
  )
  console.log(
    `${getTimestamp()} [parseAIStream] Final text buffer length:`,
    textBuffer.length,
  )
  console.log(
    `${getTimestamp()} [parseAIStream] Text buffer ending:`,
    textBuffer.slice(-300),
  )
  console.log(
    `${getTimestamp()} [parseAIStream] 🔍 Assistant tag check at end:`,
    {
      hasOpenTag: textBuffer.includes('<assistant>'),
      hasCloseTag: textBuffer.includes('</assistant>'),
      openTagIndex: textBuffer.indexOf('<assistant>'),
      closeTagIndex: textBuffer.indexOf('</assistant>'),
      wasProcessed: state.hasProcessedAssistantMessage,
    },
  )

  return textBuffer
}
